import React, { useState, useEffect } from 'react';
import Icon from '../../../../components/AppIcon';
import { 
  vendorMasterService,
  getVendorTypeOptions,
  getVendorStatusOptions
} from '../../../../api/accounting';
import { toast } from 'react-toastify';
import { VendorFormModal, VendorLedgerModal, VendorPaymentsModal } from './VendorModals';

const VendorMasterTab = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('view'); // 'view', 'create', 'edit'
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [showPaymentsModal, setShowPaymentsModal] = useState(false);
  const [ledgerData, setLedgerData] = useState([]);
  const [paymentsData, setPaymentsData] = useState([]);
  
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    vendorType: '',
    page: 1,
    limit: 50,
    sortBy: 'vendorName',
    sortOrder: 'asc'
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 50
  });

  const [formData, setFormData] = useState({
    vendorName: '',
    displayName: '',
    vendorType: 'supplier',
    contactPerson: {
      name: '',
      designation: '',
      email: '',
      phone: '',
      mobile: ''
    },
    addresses: [{
      type: 'billing',
      street: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India',
      isPrimary: true
    }],
    paymentTerms: 'net_30',
    creditLimit: 0,
    openingBalance: 0,
    gstRegistration: {
      type: 'regular',
      gstNumber: '',
      state: '',
      registrationDate: ''
    },
    panNumber: '',
    tanNumber: '',
    bankDetails: [],
    status: 'active',
    notes: ''
  });

  useEffect(() => {
    loadVendors();
    loadStatistics();
  }, [filters]);

  const loadVendors = async () => {
    try {
      setLoading(true);
      const response = await vendorMasterService.getAll(filters);
      setVendors(response.data || []);
      setPagination(response.pagination || {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 50
      });
    } catch (error) {
      console.error('Error loading vendors:', error);
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const stats = await vendorMasterService.getStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleCreateVendor = () => {
    setFormData({
      vendorName: '',
      displayName: '',
      vendorType: 'supplier',
      contactPerson: {
        name: '',
        designation: '',
        email: '',
        phone: '',
        mobile: ''
      },
      addresses: [{
        type: 'billing',
        street: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India',
        isPrimary: true
      }],
      paymentTerms: 'net_30',
      creditLimit: 0,
      openingBalance: 0,
      gstRegistration: {
        type: 'regular',
        gstNumber: '',
        state: '',
        registrationDate: ''
      },
      panNumber: '',
      tanNumber: '',
      bankDetails: [],
      status: 'active',
      notes: ''
    });
    setModalMode('create');
    setShowModal(true);
  };

  const handleEditVendor = (vendor) => {
    setSelectedVendor(vendor);
    setFormData({
      vendorName: vendor.vendorName || '',
      displayName: vendor.displayName || '',
      vendorType: vendor.vendorType || 'supplier',
      contactPerson: vendor.contactPerson || {
        name: '',
        designation: '',
        email: '',
        phone: '',
        mobile: ''
      },
      addresses: vendor.addresses || [{
        type: 'billing',
        street: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India',
        isPrimary: true
      }],
      paymentTerms: vendor.paymentTerms || 'net_30',
      creditLimit: vendor.creditLimit || 0,
      openingBalance: vendor.openingBalance || 0,
      gstRegistration: vendor.gstRegistration || {
        type: 'regular',
        gstNumber: '',
        state: '',
        registrationDate: ''
      },
      panNumber: vendor.panNumber || '',
      tanNumber: vendor.tanNumber || '',
      bankDetails: vendor.bankDetails || [],
      status: vendor.status || 'active',
      notes: vendor.notes || ''
    });
    setModalMode('edit');
    setShowModal(true);
  };

  const handleViewVendor = async (vendor) => {
    try {
      const fullVendor = await vendorMasterService.getById(vendor._id);
      setSelectedVendor(fullVendor);
      setModalMode('view');
      setShowModal(true);
    } catch (error) {
      console.error('Error loading vendor details:', error);
      toast.error('Failed to load vendor details');
    }
  };

  const handleViewLedger = async (vendor) => {
    try {
      setSelectedVendor(vendor);
      const response = await vendorMasterService.getLedger(vendor._id);
      setLedgerData(response.data || []);
      setShowLedgerModal(true);
    } catch (error) {
      console.error('Error loading vendor ledger:', error);
      toast.error('Failed to load vendor ledger');
    }
  };

  const handleViewPayments = async (vendor) => {
    try {
      setSelectedVendor(vendor);
      const response = await vendorMasterService.getPayments(vendor._id);
      setPaymentsData(response.data || []);
      setShowPaymentsModal(true);
    } catch (error) {
      console.error('Error loading vendor payments:', error);
      toast.error('Failed to load vendor payments');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      if (modalMode === 'create') {
        await vendorMasterService.create(formData);
        toast.success('Vendor created successfully');
      } else if (modalMode === 'edit') {
        await vendorMasterService.update(selectedVendor._id, formData);
        toast.success('Vendor updated successfully');
      }
      
      setShowModal(false);
      loadVendors();
      loadStatistics();
    } catch (error) {
      console.error('Error saving vendor:', error);
      toast.error(error.response?.data?.message || 'Failed to save vendor');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (vendor) => {
    if (!window.confirm(`Are you sure you want to deactivate vendor "${vendor.vendorName}"?`)) {
      return;
    }

    try {
      setLoading(true);
      await vendorMasterService.delete(vendor._id);
      toast.success('Vendor deactivated successfully');
      loadVendors();
      loadStatistics();
    } catch (error) {
      console.error('Error deleting vendor:', error);
      toast.error('Failed to deactivate vendor');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      blacklisted: 'bg-red-100 text-red-800',
      pending_approval: 'bg-yellow-100 text-yellow-800'
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Vendor Management</h3>
          <p className="text-gray-600">Manage vendor information, contacts, and payment terms</p>
        </div>
        <button
          onClick={handleCreateVendor}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <Icon name="Plus" size={16} className="mr-2" />
          Add Vendor
        </button>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Vendors</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.total || 0}</p>
              </div>
              <Icon name="Users" size={32} className="text-blue-600" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Vendors</p>
                <p className="text-2xl font-bold text-green-600">
                  {statistics.byStatus?.find(s => s._id === 'active')?.count || 0}
                </p>
              </div>
              <Icon name="CheckCircle" size={32} className="text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Medicine Suppliers</p>
                <p className="text-2xl font-bold text-purple-600">
                  {statistics.byType?.find(t => t._id === 'medicine_supplier')?.count || 0}
                </p>
              </div>
              <Icon name="Package" size={32} className="text-purple-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Equipment Suppliers</p>
                <p className="text-2xl font-bold text-orange-600">
                  {statistics.byType?.find(t => t._id === 'equipment_supplier')?.count || 0}
                </p>
              </div>
              <Icon name="Wrench" size={32} className="text-orange-600" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <div className="relative">
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search vendors..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              <Icon name="Search" size={16} className="absolute left-3 top-3 text-gray-400" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vendor Type
            </label>
            <select
              value={filters.vendorType}
              onChange={(e) => handleFilterChange('vendorType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Types</option>
              {getVendorTypeOptions().map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Status</option>
              {getVendorStatusOptions().map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sort By
            </label>
            <select
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="vendorName">Vendor Name</option>
              <option value="vendorCode">Vendor Code</option>
              <option value="createdAt">Created Date</option>
              <option value="currentOutstanding">Outstanding Amount</option>
            </select>
          </div>
        </div>
      </div>

      {/* Vendors Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Icon name="Loader2" size={32} className="animate-spin text-blue-600" />
          </div>
        ) : vendors.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Users" size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No vendors found</h3>
            <p className="text-gray-600">Get started by adding your first vendor.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vendor Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vendor Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment Terms
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Outstanding
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {vendors.map((vendor) => (
                    <tr key={vendor._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {vendor.vendorCode}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{vendor.vendorName}</div>
                        <div className="text-sm text-gray-500">{vendor.displayName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {vendor.vendorType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{vendor.contactPerson?.name}</div>
                        <div className="text-sm text-gray-500">{vendor.contactPerson?.phone}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {vendor.paymentTerms?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(vendor.currentOutstanding)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(vendor.status)}`}>
                          {vendor.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleViewVendor(vendor)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <Icon name="Eye" size={16} />
                          </button>
                          <button
                            onClick={() => handleEditVendor(vendor)}
                            className="text-green-600 hover:text-green-900"
                            title="Edit"
                          >
                            <Icon name="Edit" size={16} />
                          </button>
                          <button
                            onClick={() => handleViewLedger(vendor)}
                            className="text-purple-600 hover:text-purple-900"
                            title="View Ledger"
                          >
                            <Icon name="BookOpen" size={16} />
                          </button>
                          <button
                            onClick={() => handleViewPayments(vendor)}
                            className="text-orange-600 hover:text-orange-900"
                            title="View Payments"
                          >
                            <Icon name="CreditCard" size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(vendor)}
                            className="text-red-600 hover:text-red-900"
                            title="Deactivate"
                          >
                            <Icon name="Trash2" size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(filters.page - 1)}
                      disabled={filters.page === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(filters.page + 1)}
                      disabled={filters.page === pagination.totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{((filters.page - 1) * filters.limit) + 1}</span> to{' '}
                        <span className="font-medium">
                          {Math.min(filters.page * filters.limit, pagination.totalItems)}
                        </span> of{' '}
                        <span className="font-medium">{pagination.totalItems}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => handlePageChange(filters.page - 1)}
                          disabled={filters.page === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          <Icon name="ChevronLeft" size={16} />
                        </button>
                        {[...Array(pagination.totalPages)].map((_, i) => {
                          const pageNum = i + 1;
                          if (
                            pageNum === 1 ||
                            pageNum === pagination.totalPages ||
                            (pageNum >= filters.page - 1 && pageNum <= filters.page + 1)
                          ) {
                            return (
                              <button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                  pageNum === filters.page
                                    ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          } else if (pageNum === filters.page - 2 || pageNum === filters.page + 2) {
                            return <span key={pageNum} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">...</span>;
                          }
                          return null;
                        })}
                        <button
                          onClick={() => handlePageChange(filters.page + 1)}
                          disabled={filters.page === pagination.totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          <Icon name="ChevronRight" size={16} />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Remaining modals continue in next part... */}
      
      {/* Vendor Form Modal */}
      <VendorFormModal
        show={showModal}
        onClose={() => setShowModal(false)}
        mode={modalMode}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        loading={loading}
        selectedVendor={selectedVendor}
      />

      {/* Ledger Modal */}
      <VendorLedgerModal
        show={showLedgerModal}
        onClose={() => setShowLedgerModal(false)}
        vendor={selectedVendor}
        ledgerData={ledgerData}
      />

      {/* Payments Modal */}
      <VendorPaymentsModal
        show={showPaymentsModal}
        onClose={() => setShowPaymentsModal(false)}
        vendor={selectedVendor}
        paymentsData={paymentsData}
      />
    </div>
  );
};

export default VendorMasterTab;